import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useHomePage } from "../lib/hooks/useHomePage";
import { useFeaturedEvents } from "../lib/hooks/useFeaturedEvents";
import { useNewsList } from "../lib/hooks/useNews";
import { buildHeroBackgroundImageProps } from "../lib/sanity-image-urls";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { EventCard } from "../components/events/EventCard";
import { NewsCard } from "../components/news/NewsCard";
import { PortableTextContent } from "../components/news/PortableTextContent";
import type { Event } from "../lib/types";
import { Seo } from "../components/Seo";

export function Home() {
  const { data: homePageData, isLoading: isHomeLoading } = useHomePage();
  const { data: featuredEventsResponse, isLoading: isEventsLoading } = useFeaturedEvents();
  const { data: latestNews, isLoading: isNewsLoading } = useNewsList(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const homePage = homePageData?.homePage;
  const settings = homePageData?.settings;
  const featuredEvents = (featuredEventsResponse?.data || []) as Event[];
  const newsItems = (latestNews || []).slice(0, 4);

  // Implement carousel for background images
  const backgroundImages = settings?.backgroundImages || [];
  
  useEffect(() => {
    if (backgroundImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  if (isHomeLoading) {
    return <div className="text-center py-12 text-gray-600">Loading...</div>;
  }

  const heroFallback =
    backgroundImages.length === 0 && settings?.backgroundImages?.[0]
      ? buildHeroBackgroundImageProps(settings.backgroundImages[0])
      : null;

  const clubSections = homePage?.homePageSection?.filter((s) => s.published) ?? [];

  return (
    <div className="flex flex-col gap-16 pb-12">
      <Seo
        title="Home"
        description="North Lincolnshire and Humberside Sailing Club — book sailing events, training and club activities. View featured events and latest news."
      />
      {/* Hero Section */}
      <section className="relative bg-primary-900 text-white overflow-hidden min-h-[480px] flex items-start pt-20">
        {backgroundImages.length > 0 ? (
          backgroundImages.map((image, index) => {
            const heroImg = buildHeroBackgroundImageProps(image);
            const isActive = index === currentImageIndex;
            const isLcpCandidate = index === 0;
            return (
              <div
                key={image.asset?._id || index}
                className={`absolute inset-0 z-0 transition-opacity duration-1000 ease-in-out ${
                  isActive ? "opacity-100" : "opacity-0"
                }`}
              >
                <img
                  src={heroImg.src}
                  srcSet={heroImg.srcSet}
                  sizes={heroImg.sizes}
                  alt="North Lincolnshire and Humberside Sailing Club"
                  className="w-full h-full object-cover"
                  fetchPriority={isLcpCandidate ? "high" : "low"}
                  loading={isLcpCandidate ? "eager" : "lazy"}
                  decoding="async"
                />
              </div>
            );
          })
        ) : (
          heroFallback && (
            <div className="absolute inset-0 z-0">
              <img
                src={heroFallback.src}
                srcSet={heroFallback.srcSet}
                sizes={heroFallback.sizes}
                alt="North Lincolnshire and Humberside Sailing Club"
                className="w-full h-full object-cover"
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
            </div>
          )
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left w-full flex flex-col items-start pt-12">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 drop-shadow-lg">
            Welcome to NLHSC
          </h1>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" asChild className="bg-primary-600 hover:bg-primary-700 text-white shadow-lg border-0">
              <Link to="/events">View Upcoming Events</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Events */}
      {isEventsLoading ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <p className="text-center text-gray-600 py-8">Loading events…</p>
        </section>
      ) : featuredEvents.length > 0 ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between mb-8 gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <h2 className="text-3xl font-bold text-gray-900 whitespace-nowrap m-0 leading-tight">Featured Events</h2>
              <div className="flex-1 flex items-center min-w-0 self-stretch" aria-hidden>
                <div className="h-px w-full bg-gray-300 translate-y-0.5" />
              </div>
            </div>
            <Link to="/events" className="text-primary-600 hover:text-primary-700 font-medium flex items-center hidden sm:flex shrink-0">
              View all events <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {featuredEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Button variant="outline" asChild>
              <Link to="/events">View All Events</Link>
            </Button>
          </div>
        </section>
      ) : null}

      {/* Latest News */}
      {isNewsLoading ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <p className="text-center text-gray-600 py-8">Loading news…</p>
        </section>
      ) : newsItems.length > 0 ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between mb-8 gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <h2 className="text-3xl font-bold text-gray-900 whitespace-nowrap m-0 leading-tight">Latest News</h2>
              <div className="flex-1 flex items-center min-w-0 self-stretch" aria-hidden>
                <div className="h-px w-full bg-gray-300 translate-y-0.5" />
              </div>
            </div>
            <Link to="/news" className="text-primary-600 hover:text-primary-700 font-medium flex items-center hidden sm:flex shrink-0">
              Read all news <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {newsItems.map((article) => (
              <NewsCard key={article._id} article={article} />
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Button variant="outline" asChild>
              <Link to="/news">Read All News</Link>
            </Button>
          </div>
        </section>
      ) : null}

      {/* Club Information sections - cards in grid */}
      {clubSections.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {clubSections.map((section) => (
              <Card
                key={section._key}
                variant="elevated"
                className="flex flex-col rounded-2xl p-6 md:p-8 h-full"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <div className="prose prose-lg max-w-none text-gray-600 flex-1 min-h-0">
                  <PortableTextContent value={section.content} />
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
